import {
  createContext,
  createElement,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Bugsnag from '@bugsnag/browser';
import { useLazyQuery } from '@apollo/client';
import { alpha, createTheme } from '@mui/material/styles';
// Only WLs that carry frontend-only pieces (Footer/businessFilter/tileIcons)
// which cannot currently live in the database are imported here.
// All data-only settings are stored on the backend and fetched at runtime.
import { frontendPieces as fusePieces } from '../../WhiteLabelSettings/FuseInsurance/settings';
import { frontendPieces as cannabisPieces } from '../../WhiteLabelSettings/CannabisInsurance/settings';
import { frontendPieces as vapePieces } from '../../WhiteLabelSettings/VapeInsurance/settings';
import { frontendPieces as lastnamePieces } from '../../WhiteLabelSettings/LastnameInsurance/settings';
import { frontendPieces as engineerPieces } from '../../WhiteLabelSettings/EnigneerInsurance/settings';
import { frontendPieces as rockChainPieces } from '../../WhiteLabelSettings/RockChainInsurance/settings';
import { frontendPieces as homeAutoTravelPieces } from '../../WhiteLabelSettings/HomeAutoTravel/settings';
import { frontendPieces as youngHaggisPieces } from '../../WhiteLabelSettings/YoungHaggis/settings';
import { frontendPieces as oracleRmsPieces } from '../../WhiteLabelSettings/OracleRMS/settings';
import { frontendPieces as vagoPieces } from '../../WhiteLabelSettings/VagoAssure/settings';
import { frontendPieces as axisPieces } from '../../WhiteLabelSettings/Axis/settings';
import {
  walnutNeoFrontend,
  walnutCommercialFrontend,
  walnutAtbFrontend,
  walnutMerchantGrowthFrontend,
} from '../../WhiteLabelSettings/Walnut';
import {
  INSURANCE_TYPES,
  FrontendOnlyPieces,
  WebsiteSettings,
} from './WebsiteSettings';
import { GET_PUBLIC_WHITE_LABEL_SETTINGS } from '../../queries/whiteLabelSettings';
import {
  normalizeWhiteLabelColourForMui,
  toWhiteLabelCustomButtonFormRows,
} from '../../components/ManagementPanel/pages/dashboard/WhiteLabelSettings/whiteLabelSettingsFormHelpers';
import type { GetPublicWhiteLabelSettingsQuery } from '../../gql/graphql';
import { errorify } from '../../util';

type PublicWhiteLabelSettings = NonNullable<
  GetPublicWhiteLabelSettingsQuery['publicWhiteLabelSettings']
>;
type PublicWhiteLabelPalette = NonNullable<PublicWhiteLabelSettings['palette']>;
type PublicWhiteLabelPaletteColour =
  | PublicWhiteLabelPalette['primary']
  | PublicWhiteLabelPalette['secondary']
  | PublicWhiteLabelPalette['success']
  | PublicWhiteLabelPalette['error']
  | PublicWhiteLabelPalette['warning']
  | PublicWhiteLabelPalette['info'];
const HEADING_LEVELS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;
type HeadingLevel = typeof HEADING_LEVELS[number];
type RuntimeTypographyOverrides = Partial<
  Record<HeadingLevel, { fontFamily: string }>
> & {
  fontFamily?: string;
  subtitle1?: { fontWeight: number };
  subtitle2?: { fontWeight: number };
};

// Shown while the public white-label query is still in-flight, before the
// real settings arrive from the backend.
const FALLBACK_THEME = createTheme();
attachNoopCustomShadows(FALLBACK_THEME);
const FALLBACK_SETTINGS = new WebsiteSettings({
  companyTheme: FALLBACK_THEME,
  types: [...INSURANCE_TYPES],
});

// All WLs that carry frontend-only pieces (Footer/businessFilter/tileIcons)
// that cannot currently be stored in the database. Indexed by slug so that
// backend responses can be merged on top by matching slug. Adding a new WL
// with frontend-only behaviour is just a matter of extending this list.
// Data-only WLs are intentionally absent because runtime settings come from
// the backend response.
const frontendOnlyBySlug = new Map<string, FrontendOnlyPieces>(
  [
    fusePieces,
    cannabisPieces,
    vapePieces,
    lastnamePieces,
    engineerPieces,
    rockChainPieces,
    homeAutoTravelPieces,
    youngHaggisPieces,
    oracleRmsPieces,
    vagoPieces,
    walnutAtbFrontend,
    walnutNeoFrontend,
    walnutCommercialFrontend,
    walnutMerchantGrowthFrontend,
    axisPieces,
  ].map((pieces) => [pieces.slug, pieces]),
);

/**
 * Returns white label settings based on the current URL.
 *
 * Flow:
 * - start with neutral defaults so the UI can render immediately
 * - fetch the real settings from the backend via `useLazyQuery` and merge
 *   them on top of any matching frontend-only pieces (Footer, tileIcons,
 *   businessFilter, ...) from `frontendOnlyBySlug` by slug
 *
 * When `?whitelabel=<slug>` is present, the slug is forwarded to the backend
 * resolver, which decides (based on the request origin) whether to honour the
 * override. The frontend never pre-selects a whitelabel based on this param
 * to prevent any host from rendering another WL's styling before the backend
 * has validated the request.
 */
type SettingsStatus = {
  loading: boolean;
  ready: boolean;
};

type SettingsContextValue = SettingsStatus & {
  settings: WebsiteSettings;
};

const DEFAULT_SETTINGS_CONTEXT: SettingsContextValue = {
  settings: FALLBACK_SETTINGS,
  loading: false,
  ready: true,
};

const SettingsContext = createContext<SettingsContextValue>(
  DEFAULT_SETTINGS_CONTEXT,
);

const SETTINGS_READY_TIMEOUT_MS = 5000;

export function SettingsProvider({ children }: PropsWithChildren<{}>) {
  const settingsState = useResolvedSettings();

  return createElement(
    SettingsContext.Provider,
    { value: settingsState },
    children,
  );
}

export const useSettings: () => SettingsContextValue = () => {
  return useContext(SettingsContext);
};

function useResolvedSettings(): SettingsContextValue {
  const queryParamSlug = getQueryParamSettingsOverride();
  const isTest = process.env.NODE_ENV === 'test';
  const [timedOut, setTimedOut] = useState(false);

  const [loadPublicSettings, { data: publicData, loading, error, called }] =
    useLazyQuery(GET_PUBLIC_WHITE_LABEL_SETTINGS);

  useEffect(() => {
    if (isTest) return;

    void loadPublicSettings({
      variables: { slug: queryParamSlug },
    });
  }, [isTest, loadPublicSettings, queryParamSlug]);

  useEffect(() => {
    if (isTest || timedOut || (called && !loading && error == null)) return;

    const timeout = setTimeout(() => {
      setTimedOut(true);
    }, SETTINGS_READY_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [called, error, isTest, loading, timedOut]);

  // Derive settings synchronously from the current query data.
  // Avoiding useState + useEffect here eliminates the async state-update cycle
  // that previously caused infinite re-renders: setSettings triggered a
  // re-render → Apollo returned a new `publicData` reference → useEffect ran
  // again → setSettings again → loop.
  const settings = useMemo(() => {
    const publicSettings = publicData?.publicWhiteLabelSettings;
    if (publicSettings == null) return FALLBACK_SETTINGS;
    try {
      return buildRuntimeSettings(publicSettings);
    } catch (e) {
      Bugsnag.notify(errorify(e));
      return FALLBACK_SETTINGS;
    }
  }, [publicData]);

  // In tests the lazy query is not called, so fallback settings must remain usable.
  // Consider the settings "ready" once the request has settled, even if the
  // backend returns null.
  const ready = isTest || timedOut || (called && !loading && error == null);

  return {
    loading: !ready,
    ready,
    settings,
  };
}

// Returns the value of the `?whitelabel=<slug>` query parameter (or null when
// absent). The slug is forwarded to the backend, which decides whether the
// requesting origin is allowed to perform slug-based overrides.
function getQueryParamSettingsOverride(): string | null {
  const whitelabelFromParams = new URLSearchParams(window.location.search).get(
    'whitelabel',
  );
  if (!whitelabelFromParams) return null;
  return whitelabelFromParams;
}

function buildRuntimeSettings(
  publicSettings: PublicWhiteLabelSettings,
): WebsiteSettings {
  // Any static WebsiteSettings on the frontend that share the slug returned
  // by the backend carries over frontend-only pieces (Footer, businessFilter,
  // tileIcons, ...) that cannot currently live in the database. Everything
  // else comes from the backend response.
  const frontendOnly = frontendOnlyBySlug.get(publicSettings.slug);
  const normalizedTypes = normalizeInsuranceTypes(publicSettings.types);
  const companyTheme = buildRuntimeTheme(publicSettings);

  return new WebsiteSettings({
    ...publicSettings,
    homepage: publicSettings.homepage ?? undefined,
    googleAnalyticsToken: publicSettings.googleAnalyticsToken ?? undefined,
    types: normalizedTypes,
    customButtonList: toWhiteLabelCustomButtonFormRows(
      publicSettings.customButtons,
    ),
    logo: pickLogoData(publicSettings.logo),
    favicon: pickLogoData(publicSettings.favicon),
    mobileLogo: pickLogoData(publicSettings.mobileLogo),
    squareLogo: pickLogoData(publicSettings.squareLogo),
    headerLogo: pickLogoData(publicSettings.headerLogo),
    footerLogo: pickLogoData(publicSettings.footerLogo),
    companyTheme,
    // Frontend-only pieces — fall back to WebsiteSettings' built-in defaults
    // when there is no matching static settings on the frontend.
    businessFilter: frontendOnly?.businessFilter,
    tileIcons: frontendOnly?.tileIcons,
    Footer: frontendOnly?.Footer,
  });
}

function pickLogoData(
  logoSlot:
    | PublicWhiteLabelSettings['logo']
    | PublicWhiteLabelSettings['favicon']
    | PublicWhiteLabelSettings['mobileLogo']
    | PublicWhiteLabelSettings['squareLogo']
    | PublicWhiteLabelSettings['headerLogo']
    | PublicWhiteLabelSettings['footerLogo']
    | null
    | undefined,
): string | undefined {
  if (logoSlot == null) return undefined;
  return logoSlot.logoData || undefined;
}

function normalizeInsuranceTypes(
  types: PublicWhiteLabelSettings['types'],
): WebsiteSettings['types'] {
  const normalized: WebsiteSettings['types'] = [];
  for (const row of types) {
    const match = INSURANCE_TYPES.find((known) => known === row.type);
    if (match != null) normalized.push(match);
  }
  return normalized;
}

function buildRuntimeTheme(publicSettings: PublicWhiteLabelSettings) {
  const paletteOverrides: Record<string, unknown> = {};
  if (publicSettings.palette != null) {
    const primary = toRuntimePaletteColour(
      publicSettings.palette.primary,
      publicSettings.palette.primaryContrastText,
    );
    const secondary = toRuntimePaletteColour(
      publicSettings.palette.secondary,
      publicSettings.palette.secondaryContrastText,
    );
    const success = toRuntimePaletteColour(publicSettings.palette.success);
    const error = toRuntimePaletteColour(publicSettings.palette.error);
    const warning = toRuntimePaletteColour(publicSettings.palette.warning);
    const info = toRuntimePaletteColour(publicSettings.palette.info);
    if (primary) paletteOverrides.primary = primary;
    if (secondary) paletteOverrides.secondary = secondary;
    if (success) paletteOverrides.success = success;
    if (error) paletteOverrides.error = error;
    if (warning) paletteOverrides.warning = warning;
    if (info) paletteOverrides.info = info;
  }

  const typographyOverrides: RuntimeTypographyOverrides = {};
  if (publicSettings.typography != null) {
    // The backend returns the CSS font-family string directly via the
    // `family` field (e.g. "Inter, sans-serif"), so no name→family lookup
    // is necessary on the frontend.
    const baseFont = publicSettings.typography.fontFamily?.family;
    if (baseFont) typographyOverrides.fontFamily = baseFont;

    for (const heading of HEADING_LEVELS) {
      const headingFont = publicSettings.typography[heading]?.family;
      if (!headingFont) continue;
      typographyOverrides[heading] = { fontFamily: headingFont };
    }

    if (
      typeof publicSettings.typography.subtitleFontWeight === 'number' &&
      Number.isFinite(publicSettings.typography.subtitleFontWeight)
    ) {
      typographyOverrides.subtitle1 = {
        fontWeight: publicSettings.typography.subtitleFontWeight,
      };
      typographyOverrides.subtitle2 = {
        fontWeight: publicSettings.typography.subtitleFontWeight,
      };
    }
  }

  const theme = createTheme({
    ...(Object.keys(paletteOverrides).length > 0
      ? { palette: paletteOverrides }
      : {}),
    ...(Object.keys(typographyOverrides).length > 0
      ? { typography: typographyOverrides }
      : {}),
  });

  if (publicSettings.includeCustomShadows) {
    attachCustomShadows(theme);
  } else {
    attachNoopCustomShadows(theme);
  }
  return theme;
}

function attachNoopCustomShadows(theme: ReturnType<typeof createTheme>): void {
  Object.defineProperty(theme, 'customShadows', {
    value: {
      primary: 'none',
      secondary: 'none',
      info: 'none',
      success: 'none',
      warning: 'none',
      error: 'none',
    },
    writable: true,
    enumerable: true,
    configurable: true,
  });
}

function attachCustomShadows(theme: ReturnType<typeof createTheme>): void {
  const customShadows = {
    primary: `0 8px 16px 0 ${alpha(theme.palette.primary.main, 0.24)}`,
    secondary: `0 8px 16px 0 ${alpha(theme.palette.secondary.main, 0.24)}`,
    info: `0 8px 16px 0 ${alpha(theme.palette.info.main, 0.24)}`,
    success: `0 8px 16px 0 ${alpha(theme.palette.success.main, 0.24)}`,
    warning: `0 8px 16px 0 ${alpha(theme.palette.warning.main, 0.24)}`,
    error: `0 8px 16px 0 ${alpha(theme.palette.error.main, 0.24)}`,
  };
  Object.defineProperty(theme, 'customShadows', {
    value: customShadows,
    writable: true,
    enumerable: true,
    configurable: true,
  });
}

/**
 * Converts a backend palette colour object into the shape that MUI's
 * `createTheme` expects, stripping out empty or unsupported colour values.
 *
 * Why not pass the backend object directly?
 * The backend stores colours as plain strings, so old data can contain values
 * like `white` that CSS accepts but MUI's colour utilities cannot parse.
 * Filtering invalid shades here lets MUI compute any missing shade values.
 */
function toRuntimePaletteColour(
  colour: PublicWhiteLabelPaletteColour | null | undefined,
  contrastText?: string | null,
): Record<string, string> | null {
  if (colour == null) return null;

  const main = normalizeWhiteLabelColourForMui(colour.main);
  if (main == null) return null;

  const out: Record<string, string> = { main };
  const lighter = normalizeWhiteLabelColourForMui(colour.lighter);
  const light = normalizeWhiteLabelColourForMui(colour.light);
  const dark = normalizeWhiteLabelColourForMui(colour.dark);
  const darker = normalizeWhiteLabelColourForMui(colour.darker);
  const normalizedContrastText = normalizeWhiteLabelColourForMui(contrastText);

  if (lighter != null) out.lighter = lighter;
  if (light != null) out.light = light;
  if (dark != null) out.dark = dark;
  if (darker != null) out.darker = darker;
  if (normalizedContrastText != null) out.contrastText = normalizedContrastText;
  return out;
}
